import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useForm, Controller } from "react-hook-form";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { joiResolver } from "@hookform/resolvers/joi";
import { formValuesToSave } from "./utils/formValuesToSave";
import { createValidationSchema } from "./utils/validationSchema";
import { getDefaultValues } from "./utils/getDefaultValues";
import {
  useGetEntityPreferencesByPath,
  useUpdateEntityPreferencesByPath,
  useCreateEntityPreferencesByPath,
  useGetEntity,
  useGetEntityPreferencesWithSpecialKey,
} from "../../../../hooks/entity";
import useAuth from "../../../../hooks/useAuth";
import { useCorrectMessage, useEmailPreview } from "../../../../hooks/messages";
import { useUploadFile } from "../../../../hooks/cdn";
import { toast } from "react-toastify";
import { CoreFieldsSection } from "./sections/CoreFieldsSection";
import { CustomQuestionsSection } from "./sections/CustomQuestionsSection";
import { PostRegistrationActionsSection } from "./sections/PostRegistrationActionsSection";
import { PaymentSection } from "./sections/PaymentSection";
import { AutomatedEmailSection } from "./sections/AutomatedEmailSection";
import { EsignatureSection } from "./sections/EsignatureSection";
import { BookingSection } from "./sections/BookingSection";
import { BookingHandoverPreview } from "./sections/BookingHandoverPreview";
import { UtmTrackingSection } from "./sections/UtmTrackingSection";
import { CustomButton } from "../../../atoms/CustomButton";
import { CharacterCounter } from "../../../atoms/CharacterCounter";
import { RegistrationFormData } from "./types";
import { Textarea } from "flowbite-react";
import { Surface } from "../../../atoms/Surface";
import { Spinner } from "../../../atoms/Spinner";
import { ModalShell } from "../../../atoms/ModalShell";
import EmailPreviewFrame from "../../../atoms/EmailPreviewFrame";
import { toEditorHtml } from "../../../atoms/HtmlEditor";
import { apiCache } from "../../../../utils/cacheManager";
import { useGetCustomRegisterProcessId } from "../../../../hooks/users";
import {
  Step1AuthMethod,
  Step2RegistrationForm,
  Step3CustomQuestions,
  Step4Payment,
} from "../../customRegister/components";
import { CustomRegistrationFormData } from "../../customRegister/types";
import ShareModal from "./sections/ShareModal";
import { HiUserAdd, HiArrowLeft } from "react-icons/hi";
import { MdOutlineExitToApp } from "react-icons/md";
import { ResponsivePreviewLayout } from "../../../atoms/ResponsivePreviewLayout";
import {
  cleanHtmlForEmail,
  EMAIL,
} from "../entities/sendPersonalMessage/helpers";
import { TEMPLATE_KEYS } from "../entities/sendPersonalMessage/constants";
import { usePermissions } from "../../../../utils/permissions";
import { Cap, Level } from "../../../../utils/rbacCatalog";
import { PlansModal } from "../../../modals/PlansModal";
import { PlanTier } from "../../../../utils/permissions";
import {
  useGetAgendaTypes,
  useUpdateAgendaType,
} from "../../../../hooks/agenda";
import { agendaRights } from "../../../../hooks/agenda/useAgendaAccess";

const PREVIEW_BOOKING_STEP = 6;

const SectionHeading = ({
  step,
  label,
  first = false,
}: {
  step: number;
  label: string;
  first?: boolean;
}) => (
  <h2
    className={`text-lg font-semibold text-gray-900 ${
      first ? "" : "border-t border-gray-200 pt-6"
    }`}
  >
    {step}. {label}
  </h2>
);

export default function CustomRegistrationPage() {
  const { t } = useTranslation();
  const { formId } = useParams<{ formId: string }>();
  const isDefaultForm = !formId || formId === "default";
  const preferencesPath = isDefaultForm
    ? "custom_registration"
    : `custom_registration.${formId}`;

  const navigate = useNavigate();
  const { user, customer_identification } = useAuth();
  const permissions = usePermissions(user);
  const canWrite = permissions.can(Cap.SIGNUP_FORMS, Level.WRITE);
  const hasRedirectUrlAccess = permissions.hasFeatureAccess(
    "SIGNUP_FORM_REDIRECT_URL"
  );
  const [showRedirectUpgradeModal, setShowRedirectUpgradeModal] =
    useState(false);
  const agenda = agendaRights(permissions);
  const canConfigureBooking = agenda.canView;
  const bookingStep = canConfigureBooking ? 1 : 0;
  const [searchParams] = useSearchParams();
  const bookingTypeFromLink = searchParams.get("booking_type");
  const { data: agendaTypes = [] } = useGetAgendaTypes(
    customer_identification,
    user.entity_identification,
    { enabled: canConfigureBooking }
  );
  const { mutateAsync: updateAgendaType } = useUpdateAgendaType(
    customer_identification,
    user.entity_identification
  );

  const {
    data: entityPreferences,
    isLoading: isLoadingPreferences,
    error: preferencesError,
    refetch,
  } = useGetEntityPreferencesByPath(
    customer_identification,
    user.entity_identification,
    preferencesPath
  );

  const { mutateAsync: updateEntityPreferences, isLoading: isUpdating } =
    useUpdateEntityPreferencesByPath();
  const { mutateAsync: createEntityPreferences } =
    useCreateEntityPreferencesByPath();

  const { mutateAsync: getCustomRegisterProcessId } =
    useGetCustomRegisterProcessId();

  const { mutate: correctMessage, isLoading: isCorrectingMessage } =
    useCorrectMessage();

  const { mutateAsync: uploadFile } = useUploadFile();

  const formConfig = useMemo(() => {
    if (!entityPreferences) return undefined;
    return isDefaultForm
      ? entityPreferences
      : entityPreferences?.custom_registration?.[formId!] ?? undefined;
  }, [entityPreferences, isDefaultForm, formId]);

  const isNewForm = !isDefaultForm && !isLoadingPreferences && !formConfig;

  const defaultValues = useMemo(
    () => getDefaultValues(formConfig),
    [formConfig]
  );
  const [formName, setFormName] = useState<string>("");
  const [generatedLink, setGeneratedLink] = useState<string>("");
  const [showShareModal, setShowShareModal] = useState(false);
  const [processId, setProcessId] = useState("");
  const [previewStep, setPreviewStep] = useState<number>(1);
  const [showEmailPreview, setShowEmailPreview] = useState(false);
  const [emailPreviewHtml, setEmailPreviewHtml] = useState("");

  const { mutate: requestEmailPreview, isLoading: isRequestingPreview } =
    useEmailPreview();

  const { data: cdnLogoData } = useGetEntityPreferencesByPath(
    customer_identification,
    user.entity_identification,
    "cdn_logo_project_path"
  );

  const { data: entityData } = useGetEntity(
    customer_identification,
    user.entity_identification
  );

  const { data: userFavouriteTemplate } = useGetEntityPreferencesWithSpecialKey(
    {
      customer_identification,
      entityIdentification: user.entity_identification,
      userIdentification: user.user_identification,
      key: "fav_template",
    }
  );

  const {
    control,
    handleSubmit,
    watch,
    setValue,
    setError,
    clearErrors,
    reset,
    trigger,
    formState: { errors, isSubmitting },
  } = useForm<RegistrationFormData>({
    resolver: joiResolver(createValidationSchema(t)),
    defaultValues,
    mode: "onBlur",
  });

  useEffect(() => {
    if (formConfig) {
      reset(getDefaultValues(formConfig));
      if (!isDefaultForm && formConfig.name) {
        setFormName(formConfig.name);
      }
    } else if (isNewForm) {
      setFormName(t("signupForms.newFormDefaultName") as string);
      if (bookingTypeFromLink && canConfigureBooking) {
        setValue("booking", { enabled: true, type_id: bookingTypeFromLink });
      }
    }
  }, [
    formConfig,
    reset,
    isDefaultForm,
    isNewForm,
    t,
    bookingTypeFromLink,
    canConfigureBooking,
    setValue,
  ]);

  const linkNewFormToBookingType = useCallback(
    async (savedBookingTypeId?: string) => {
      if (!isNewForm || !formId || !agenda.canConfigure) return;
      if (!bookingTypeFromLink || savedBookingTypeId !== bookingTypeFromLink)
        return;
      const type = agendaTypes.find((item) => item.id === bookingTypeFromLink);
      if (!type || type.signup_form_id) return;
      try {
        await updateAgendaType({
          id: type.id,
          patch: { signup_form_id: formId },
        });
      } catch {
        toast.error(t("customRegistration.booking.linkTypeError"));
      }
    },
    [
      isNewForm,
      formId,
      agenda.canConfigure,
      bookingTypeFromLink,
      agendaTypes,
      updateAgendaType,
      t,
    ]
  );

  const formValues = watch();

  const previewConfig = useMemo(() => {
    return {
      ...formValues,
      customQuestions: formValues.customQuestions || [],
    };
  }, [formValues]);

  const previewSequence = useMemo(
    () => [
      1,
      2,
      ...(previewConfig.customQuestions.length > 0 ? [3] : []),
      ...(previewConfig.booking?.enabled ? [PREVIEW_BOOKING_STEP] : []),
      ...(previewConfig.paymentConfig?.enabled ? [4] : []),
      5,
    ],
    [
      previewConfig.customQuestions.length,
      previewConfig.booking?.enabled,
      previewConfig.paymentConfig?.enabled,
    ]
  );
  useEffect(() => {
    if (!previewSequence.includes(previewStep)) {
      setPreviewStep(previewSequence[0]);
    }
  }, [previewSequence, previewStep]);
  const nextPreviewStep = (step: number) =>
    previewSequence[previewSequence.indexOf(step) + 1] ?? 5;
  const previousPreviewStep = (step: number) =>
    previewSequence[previewSequence.indexOf(step) - 1] ?? 1;
  const nextPreviewTitle = (step: number) =>
    nextPreviewStep(step) === 5
      ? t("customRegistration.preview.complete")
      : t("customRegistration.preview.nextStep");

  const {
    control: previewControl,
    formState: { errors: previewErrors },
  } = useForm<CustomRegistrationFormData>({
    mode: "onChange",
  });

  const convertSendDelayToMinutes = (delay: string): number => {
    const delayMap: Record<string, number> = {
      immediate: 0,
      "1hour": 60,
      "6hours": 360,
      "1day": 1440,
      "2days": 2880,
    };
    return delayMap[delay] ?? 0;
  };

  const validateAndBuildEsignature = useCallback(
    (
      data: RegistrationFormData
    ): {
      enabled: boolean;
      template_id: string;
      message: string;
      expiry_days: number | string;
    } | null => {
      clearErrors("esignature.template_id");
      clearErrors("esignature.expiry_days");
      clearErrors("esignature.message");

      const templateId = (data.esignature?.template_id || "").trim();
      const message = data.esignature?.message || "";
      const rawExpiry = data.esignature?.expiry_days;

      if (!data.esignature?.enabled) {
        return {
          enabled: false,
          template_id: templateId,
          message,
          expiry_days:
            rawExpiry === undefined || rawExpiry === null
              ? ""
              : (rawExpiry as number | string),
        };
      }

      const expiry = Number(rawExpiry);

      let hasError = false;

      if (!templateId) {
        setError("esignature.template_id", {
          type: "manual",
          message: t(
            "customRegistration.validation.esignatureTemplateRequired"
          ) as string,
        });
        hasError = true;
      }

      if (!Number.isInteger(expiry) || expiry < 1 || expiry > 180) {
        setError("esignature.expiry_days", {
          type: "manual",
          message: t(
            "customRegistration.validation.esignatureExpiryRange"
          ) as string,
        });
        hasError = true;
      }

      if (message.length > 300) {
        setError("esignature.message", {
          type: "manual",
          message: t(
            "customRegistration.validation.esignatureMessageMaxLength"
          ) as string,
        });
        hasError = true;
      }

      if (hasError) return null;

      return {
        enabled: true,
        template_id: templateId,
        message,
        expiry_days: expiry,
      };
    },
    [clearErrors, setError, t]
  );

  const onSubmit = useCallback(
    async (data: RegistrationFormData) => {
      try {
        if (
          data.automatedEmail.enabled &&
          data.automatedEmail._files &&
          data.automatedEmail._files.length > 0
        ) {
          toast.info(t("customRegistration.messages.uploadingFiles"));

          const uploadPromises = data.automatedEmail._files.map(
            async (file) => {
              try {
                const response = await uploadFile({
                  user_identification: customer_identification,
                  entity_identification: user.entity_identification,
                  data: { file } as any,
                });

                const cdnUrl = response?.data?.element[0]?.cdnPath;

                if (!cdnUrl) {
                  throw new Error(`Failed to upload ${file.name}`);
                }

                return {
                  name: file.name,
                  size: file.size,
                  type: file.type,
                  url: cdnUrl,
                };
              } catch (error) {
                console.error(`Failed to upload ${file.name}:`, error);
                throw error;
              }
            }
          );

          const uploadedAttachments = await Promise.all(uploadPromises);

          const existingAttachments = data.automatedEmail.attachments || [];
          data.automatedEmail.attachments = [
            ...existingAttachments,
            ...uploadedAttachments,
          ];
          delete data.automatedEmail._files;
        }

        const dataToSave: any = {
          ...data,
          automatedEmail: {
            ...data.automatedEmail,
            sendDelay: data.automatedEmail.enabled
              ? (convertSendDelayToMinutes(
                  data.automatedEmail.sendDelay
                ) as any)
              : data.automatedEmail.sendDelay,
          },
        };

        const esignaturePayload = validateAndBuildEsignature(data);
        if (esignaturePayload === null) {
          toast.error(t("customRegistration.messages.error"));
          return;
        }
        dataToSave.esignature = esignaturePayload;

        if (!isDefaultForm) {
          dataToSave.name = formName || t("signupForms.newFormDefaultName");
          dataToSave.createdAt =
            formConfig?.createdAt || new Date().toISOString();
        }

        const savePreferences = isNewForm
          ? createEntityPreferences
          : updateEntityPreferences;
        await savePreferences({
          user_identification: customer_identification,
          entity_identification: user.entity_identification,
          path: preferencesPath,
          value: dataToSave,
        });

        await linkNewFormToBookingType(
          data.booking?.enabled ? data.booking.type_id : undefined
        );
        await refetch();

        toast.success(t("customRegistration.messages.success"));

        navigate("/control-panel/signup-forms");
      } catch (err: any) {
        console.error("Save failed:", err);
        if (!err?.response?.data?.message) {
          toast.error(t("customRegistration.messages.error"));
        }
      }
    },
    [
      customer_identification,
      updateEntityPreferences,
      createEntityPreferences,
      user.entity_identification,
      uploadFile,
      isDefaultForm,
      isNewForm,
      preferencesPath,
      formConfig,
      formName,
      t,
      refetch,
      validateAndBuildEsignature,
      linkNewFormToBookingType,
    ]
  );

  const handleGenerateLink = useCallback(async () => {
    try {
      const dataToSave: any = formValuesToSave(formValues);

      const esignaturePayload = validateAndBuildEsignature(formValues);
      if (esignaturePayload === null) {
        toast.error(t("customRegistration.messages.error"));
        return;
      }
      dataToSave.esignature = esignaturePayload;

      if (!(await trigger("booking"))) {
        toast.error(t("customRegistration.messages.error"));
        return;
      }

      if (
        dataToSave.automatedEmail.enabled &&
        dataToSave.automatedEmail._files &&
        dataToSave.automatedEmail._files.length > 0
      ) {
        toast.info(t("customRegistration.messages.uploadingFiles"));

        const uploadPromises = dataToSave.automatedEmail._files.map(
          async (file: File) => {
            try {
              const response = await uploadFile({
                user_identification: customer_identification,
                entity_identification: user.entity_identification,
                data: { file } as any,
              });

              const cdnUrl = response?.data?.element[0]?.cdnPath;

              if (!cdnUrl) {
                throw new Error(`Failed to upload ${file.name}`);
              }

              return {
                name: file.name,
                size: file.size,
                type: file.type,
                url: cdnUrl,
              };
            } catch (error) {
              console.error(`Failed to upload ${file.name}:`, error);
              throw error;
            }
          }
        );

        const uploadedAttachments = await Promise.all(uploadPromises);
        const existingAttachments = dataToSave.automatedEmail.attachments || [];
        dataToSave.automatedEmail.attachments = [
          ...existingAttachments,
          ...uploadedAttachments,
        ];
        delete dataToSave.automatedEmail._files;
      }

      if (dataToSave.automatedEmail.enabled) {
        dataToSave.automatedEmail.sendDelay = convertSendDelayToMinutes(
          dataToSave.automatedEmail.sendDelay
        ) as any;
      }

      if (!isDefaultForm) {
        dataToSave.name = formName || t("signupForms.newFormDefaultName");
        dataToSave.createdAt =
          formConfig?.createdAt || new Date().toISOString();
      }

      const savePreferences = isNewForm
        ? createEntityPreferences
        : updateEntityPreferences;
      await savePreferences({
        user_identification: customer_identification,
        entity_identification: user.entity_identification,
        path: preferencesPath,
        value: dataToSave,
      });
      setValue(
        "automatedEmail.attachments",
        dataToSave.automatedEmail.attachments
      );
      setValue("automatedEmail._files", dataToSave.automatedEmail._files);
      await linkNewFormToBookingType(
        dataToSave.booking?.enabled ? dataToSave.booking.type_id : undefined
      );
      setShowShareModal(true);
    } catch (err: any) {
      console.error("Generate link - save failed:", err);
      if (!err?.response?.data?.message) {
        toast.error(t("customRegistration.messages.error"));
      }
    }
  }, [
    formValues,
    customer_identification,
    updateEntityPreferences,
    createEntityPreferences,
    user.entity_identification,
    uploadFile,
    isDefaultForm,
    isNewForm,
    preferencesPath,
    formConfig,
    formName,
    t,
    validateAndBuildEsignature,
    trigger,
    setValue,
    linkNewFormToBookingType,
  ]);

  const getProcessId = useCallback(async (): Promise<string | null> => {
    const cacheKey = `custom_registration_process_id_${customer_identification}_${
      user.entity_identification
    }_${user.user_identification}${isDefaultForm ? "" : `_${formId}`}`;

    const cachedProcessId = apiCache.get<string>(cacheKey);
    if (cachedProcessId) {
      return cachedProcessId;
    }

    try {
      const processResponse = await getCustomRegisterProcessId({
        customer_identification,
        entity_identification: user.entity_identification,
        user_identification: user.user_identification,
        form_id: isDefaultForm ? undefined : formId,
      });

      const pid = processResponse.data.element?.process_identification;

      if (pid) {
        apiCache.setWithLongTTL(cacheKey, pid);
        return pid;
      }

      return null;
    } catch (error) {
      return null;
    }
  }, [
    customer_identification,
    user.entity_identification,
    user.user_identification,
    getCustomRegisterProcessId,
    isDefaultForm,
    formId,
  ]);

  useEffect(() => {
    if (user.entity_identification) {
      getProcessId().then((value) => {
        setProcessId(value as string);
        const businessCardUrl = `${process.env.REACT_APP_HOST_URL}/process/${value}`;
        setGeneratedLink(businessCardUrl);
      });
    }
  }, [getProcessId, user.entity_identification]);

  const handleCorrectEmailContent = useCallback(() => {
    const currentContent = watch("automatedEmail.content");

    correctMessage(
      {
        customer_identification,
        entity_identification: user.entity_identification,
        message: currentContent || "",
      },
      {
        onSuccess: (value) => {
          const answer = value?.data?.element?.answer;
          if (answer) {
            const convertMarkdownQuotesToHtml = (text: string): string => {
              const lines = text.split("\n");
              let result = "";
              let inBlockquote = false;
              let blockquoteContent = "";

              for (let i = 0; i < lines.length; i++) {
                const line = lines[i];

                if (line.startsWith(">")) {
                  const content = line.replace(/^>\s?/, "");

                  if (!inBlockquote) {
                    inBlockquote = true;
                    blockquoteContent = content;
                  } else {
                    blockquoteContent += "\n" + content;
                  }
                } else {
                  if (inBlockquote) {
                    result += `<blockquote style="border-left: 4px solid ${EMAIL.filet}; margin: 1em 0; padding-left: 1em; color: ${EMAIL.texteSecondaire}; font-style: italic;">${blockquoteContent}</blockquote>`;
                    inBlockquote = false;
                    blockquoteContent = "";
                  }
                  result += line + "\n";
                }
              }

              if (inBlockquote) {
                result += `<blockquote style="border-left: 4px solid ${EMAIL.filet}; margin: 1em 0; padding-left: 1em; color: ${EMAIL.texteSecondaire}; font-style: italic;">${blockquoteContent}</blockquote>`;
              }

              return result;
            };

            const processedContent = convertMarkdownQuotesToHtml(answer);
            setValue("automatedEmail.content", toEditorHtml(processedContent));
          }
        },
        onError: () => {
          toast.error(t("customRegistration.messages.aiCorrectionError"));
        },
      }
    );
  }, [
    watch,
    correctMessage,
    customer_identification,
    user.entity_identification,
    setValue,
    t,
  ]);

  const handlePreviewEmail = useCallback(
    (htmlContent: string) => {
      const subject = watch("automatedEmail.subject");

      if (!subject || !htmlContent) {
        return;
      }

      const cleanedHtml = cleanHtmlForEmail(htmlContent);

      const templateKeys: Record<string, string> = TEMPLATE_KEYS;
      const templateIdentifier =
        userFavouriteTemplate?.user_data_preferences?.fav_template;

      const templateName =
        templateKeys[templateIdentifier] ?? TEMPLATE_KEYS.default;

      requestEmailPreview(
        {
          entity_user_identification: customer_identification,
          entity_identification: user.entity_identification,
          template: templateName,
          data: {
            projectLogo: cdnLogoData?.cdn_logo_project_path,
            projectName: entityData?.entity_name,
            title: subject,
            messageText: cleanedHtml,
            intro: "",
            signature: "",
          },
        },
        {
          onSuccess: (response) => {
            if (response?.data) {
              setEmailPreviewHtml(response.data);
            }
            setShowEmailPreview(true);
          },
        }
      );
    },
    [
      watch,
      requestEmailPreview,
      customer_identification,
      user.entity_identification,
      cdnLogoData,
      entityData,
      userFavouriteTemplate,
    ]
  );

  if (isLoadingPreferences)
    return (
      <span translate="no">
        <Spinner size="sm" />
      </span>
    );

  const isNotFoundError = (preferencesError as any)?.response?.status === 404;
  if (preferencesError && !isNotFoundError)
    return (
      <CustomButton
        onClick={() => refetch()}
        title={t("dashboard.billings.btn-05") as string}
        variant="primary"
      />
    );

  return (
    <div className="container mx-auto p-3 sm:p-4 md:p-6 max-w-5xl">
      <div className="mb-6">
        <button
          onClick={() => navigate("/control-panel/signup-forms")}
          className="mb-3 flex items-center gap-1.5 text-sm text-gray-500 transition-colors hover:text-gray-800"
        >
          <HiArrowLeft size={16} />
          {t("signupForms.backToList")}
        </button>
        <div className="min-w-0">
          {isDefaultForm ? (
            <h1 className="text-xl font-semibold text-gray-900">
              {t("customRegistration.pageTitle")}
            </h1>
          ) : (
            <input
              type="text"
              value={formName}
              onChange={(e) => setFormName(e.target.value)}
              placeholder={t("signupForms.formNamePlaceholder") as string}
              maxLength={80}
              aria-label={t("signupForms.formNamePlaceholder") as string}
              className="w-full border-0 border-b border-transparent bg-transparent px-0 py-0.5 text-xl font-semibold text-gray-900 placeholder-gray-300 transition-colors hover:border-gray-300 focus:border-blue-500 focus:outline-none focus:ring-0"
            />
          )}
          <p className="mt-0.5 text-sm text-gray-500">
            {t("customRegistration.pageDescription")}{" "}
            {t("customRegistration.pageDescriptionNewUserOnly")}
          </p>
        </div>
      </div>

      <div className="mb-6 rounded-lg border border-gray-200 bg-white p-4">
        <div className="flex items-start gap-3">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-gray-50">
            <HiUserAdd size={18} className="text-gray-600" />
          </div>

          <div className="min-w-0 flex-1">
            <h2 className="text-sm font-semibold text-gray-900">
              {t("customRegistration.benefits.title")}
            </h2>
            <p className="mt-0.5 text-sm text-gray-500">
              {t("customRegistration.benefits.description")}
            </p>

            <ul className="mt-3 space-y-1.5 text-xs text-gray-700">
              <li className="flex items-start gap-2">
                <span className="mt-1.5 h-1 w-1 shrink-0 rounded-full bg-gray-300" />
                <span>{t("customRegistration.benefits.benefit1")}</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="mt-1.5 h-1 w-1 shrink-0 rounded-full bg-gray-300" />
                <span>{t("customRegistration.benefits.benefit2")}</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="mt-1.5 h-1 w-1 shrink-0 rounded-full bg-gray-300" />
                <span>{t("customRegistration.benefits.benefit3")}</span>
              </li>
            </ul>
          </div>
        </div>
      </div>

      <ResponsivePreviewLayout
        configColSpan={6}
        previewColSpan={6}
        showPreviewLabel={t("customRegistration.previewTitle") as string}
        hidePreviewLabel={t("common.hidePreview") as string}
        hasPreviewContent={true}
        previewContent={
          <div className="p-4">
            <div className="pb-4">
              <h2 className="text-lg font-semibold text-gray-900">
                {t("customRegistration.previewTitle")}
              </h2>
              <p className="mt-0.5 text-sm text-gray-500">
                {t("customRegistration.previewDescription")}
              </p>
            </div>

            <Surface>
              {previewStep === 1 && (
                <div className="space-y-4">
                  {previewConfig.customQuestionsPosition === "first" &&
                  previewConfig.customQuestions &&
                  previewConfig.customQuestions.length > 0 ? (
                    <>
                      <Step3CustomQuestions
                        config={previewConfig as any}
                        control={previewControl}
                        errors={previewErrors as any}
                        isSubmitting={false}
                        onSubmit={async () => {}}
                        isLastStep={false}
                        isPreview={true}
                      />
                      <div className="pt-4 border-t border-gray-200">
                        <CustomButton
                          variant="primary"
                          title={t("customRegistration.preview.nextStep")}
                          className="w-full"
                          onClick={() => setPreviewStep(2)}
                        />
                      </div>
                    </>
                  ) : (
                    <>
                      <Step1AuthMethod
                        onEmailClick={() => {}}
                        onLoginStart={() => {}}
                        onResolve={() => {}}
                        onReject={() => {}}
                        isDisabled={true}
                      />
                      <div className="pt-4 border-t border-gray-200">
                        <CustomButton
                          variant="primary"
                          title={t("customRegistration.preview.nextStep")}
                          className="w-full"
                          onClick={() => setPreviewStep(2)}
                        />
                      </div>
                    </>
                  )}
                </div>
              )}

              {previewStep === 2 && (
                <div className="space-y-4">
                  {previewConfig.customQuestionsPosition === "first" &&
                  previewConfig.customQuestions &&
                  previewConfig.customQuestions.length > 0 ? (
                    <>
                      <Step1AuthMethod
                        onEmailClick={() => {}}
                        onLoginStart={() => {}}
                        onResolve={() => {}}
                        onReject={() => {}}
                        isDisabled={true}
                      />
                      <div className="pt-4 border-t border-gray-200 flex gap-3">
                        <CustomButton
                          variant="secondary"
                          title={t("customRegistration.preview.previousStep")}
                          className="flex-1"
                          onClick={() => setPreviewStep(1)}
                        />
                        <CustomButton
                          variant="primary"
                          title={t("customRegistration.preview.nextStep")}
                          className="flex-1"
                          onClick={() => setPreviewStep(3)}
                        />
                      </div>
                    </>
                  ) : (
                    <>
                      <Step2RegistrationForm
                        config={previewConfig as any}
                        control={null}
                        errors={null}
                        type={"email"}
                        isSubmitting={false}
                        onBack={() => {}}
                        onSubmit={() => {}}
                        isLastStep={false}
                        isPreview={true}
                      />
                      <div className="pt-4 border-t border-gray-200 flex gap-3">
                        <CustomButton
                          variant="secondary"
                          title={t("customRegistration.preview.previousStep")}
                          className="flex-1"
                          onClick={() => setPreviewStep(1)}
                        />
                        <CustomButton
                          variant="primary"
                          title={nextPreviewTitle(2)}
                          className="flex-1"
                          onClick={() => setPreviewStep(nextPreviewStep(2))}
                        />
                      </div>
                    </>
                  )}
                </div>
              )}

              {previewStep === 3 && (
                <div className="space-y-4">
                  {previewConfig.customQuestionsPosition === "first" ? (
                    <>
                      <Step2RegistrationForm
                        config={previewConfig as any}
                        control={null}
                        errors={null}
                        type={"email"}
                        isSubmitting={false}
                        onBack={() => {}}
                        onSubmit={() => {}}
                        isLastStep={false}
                        isPreview={true}
                      />
                      <div className="pt-4 border-t border-gray-200 flex gap-3">
                        <CustomButton
                          variant="secondary"
                          title={t("customRegistration.preview.previousStep")}
                          className="flex-1"
                          onClick={() => setPreviewStep(2)}
                        />
                        <CustomButton
                          variant="primary"
                          title={nextPreviewTitle(3)}
                          className="flex-1"
                          onClick={() => setPreviewStep(nextPreviewStep(3))}
                        />
                      </div>
                    </>
                  ) : (
                    previewConfig.customQuestions &&
                    previewConfig.customQuestions.length > 0 && (
                      <>
                        <Step3CustomQuestions
                          config={previewConfig as any}
                          control={previewControl}
                          errors={previewErrors as any}
                          isSubmitting={false}
                          onSubmit={async () => {}}
                          isLastStep={true}
                          isPreview={true}
                        />
                        <div className="pt-4 border-t border-gray-200 flex gap-3">
                          <CustomButton
                            variant="secondary"
                            title={t("customRegistration.preview.previousStep")}
                            className="flex-1"
                            onClick={() => setPreviewStep(2)}
                          />
                          <CustomButton
                            variant="primary"
                            title={nextPreviewTitle(3)}
                            className="flex-1"
                            onClick={() => setPreviewStep(nextPreviewStep(3))}
                          />
                        </div>
                      </>
                    )
                  )}
                </div>
              )}

              {previewStep === PREVIEW_BOOKING_STEP &&
                previewConfig.booking?.enabled && (
                  <div className="space-y-4">
                    <BookingHandoverPreview
                      entityId={user.entity_identification}
                      type={agendaTypes.find(
                        (type) => type.id === previewConfig.booking?.type_id
                      )}
                    />
                    <div className="pt-4 border-t border-gray-200 flex gap-3">
                      <CustomButton
                        variant="secondary"
                        title={t("customRegistration.preview.previousStep")}
                        className="flex-1"
                        onClick={() =>
                          setPreviewStep(
                            previousPreviewStep(PREVIEW_BOOKING_STEP)
                          )
                        }
                      />
                      <CustomButton
                        variant="primary"
                        title={nextPreviewTitle(PREVIEW_BOOKING_STEP)}
                        className="flex-1"
                        onClick={() =>
                          setPreviewStep(nextPreviewStep(PREVIEW_BOOKING_STEP))
                        }
                      />
                    </div>
                  </div>
                )}

              {previewStep === 4 && previewConfig.paymentConfig?.enabled && (
                <div className="space-y-4">
                  <Step4Payment config={previewConfig as any} />
                  <div className="pt-4 border-t border-gray-200 flex gap-3">
                    <CustomButton
                      variant="secondary"
                      title={t("customRegistration.preview.previousStep")}
                      className="flex-1"
                      onClick={() => setPreviewStep(previousPreviewStep(4))}
                    />
                    <CustomButton
                      variant="primary"
                      title={t("customRegistration.preview.complete")}
                      className="flex-1"
                      onClick={() => setPreviewStep(5)}
                    />
                  </div>
                </div>
              )}

              {previewStep === 5 && (
                <div className="space-y-4">
                  {previewConfig.postRegistrationAction === "redirect" ? (
                    <div className="p-6 text-center">
                      <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                        <MdOutlineExitToApp className="w-8 h-8 text-blue-600" />
                      </div>
                      <p className="text-gray-600 mb-4">
                        {t("customRegistration.stepPreview.redirectMessage")}
                      </p>
                      {previewConfig.redirectUrl && (
                        <div className="mt-4 space-y-2">
                          <div className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                            <p
                              className="text-sm font-mono text-blue-600 break-all"
                              translate="no"
                            >
                              {(() => {
                                try {
                                  const url = new URL(
                                    previewConfig.redirectUrl
                                  );
                                  url.searchParams.append(
                                    "user_identification",
                                    "NEW_USER_ID"
                                  );
                                  url.searchParams.append(
                                    "entity_identification",
                                    user.entity_identification
                                  );
                                  return url.toString();
                                } catch {
                                  return previewConfig.redirectUrl;
                                }
                              })()}
                            </p>
                          </div>
                          <p className="text-xs text-gray-500">
                            {t("customRegistration.stepPreview.urlWithParams")}
                          </p>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="p-6 text-center">
                      <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                        <svg
                          className="w-8 h-8 text-green-600"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M5 13l4 4L19 7"
                          />
                        </svg>
                      </div>
                      <h4 className="text-xl font-semibold text-gray-900 mb-2">
                        {t("customRegistration.stepPreview.successTitle")}
                      </h4>
                      <p className="text-gray-600">
                        {t("customRegistration.stepPreview.successMessage")}
                      </p>
                    </div>
                  )}
                  <div className="pt-4 border-t border-gray-200">
                    <CustomButton
                      variant="secondary"
                      title={t("customRegistration.preview.restart")}
                      className="w-full"
                      onClick={() => setPreviewStep(1)}
                    />
                  </div>
                </div>
              )}

              <div className="mt-6 flex items-center justify-center gap-2">
                {previewSequence.map((step, index) => {
                  const currentIndex = previewSequence.indexOf(previewStep);
                  const isActive = index === currentIndex;
                  const isPast = index < currentIndex;

                  return (
                    <div
                      key={step}
                      className={`h-2 rounded-full transition-all ${
                        isActive
                          ? "w-8 bg-blue-600"
                          : isPast
                          ? "w-2 bg-green-600"
                          : "w-2 bg-gray-300"
                      }`}
                    />
                  );
                })}
              </div>
            </Surface>
          </div>
        }
      >
        <div className="p-1 sm:p-4">
          <div className="pb-4 pt-3">
            <h2 className="text-lg font-semibold text-gray-900">
              {t("customRegistration.letsConfigure")}
            </h2>
          </div>
          <SectionHeading
            step={1}
            label={t("customRegistration.coreFields.aboutForm")}
            first
          />
          <form
            onSubmit={handleSubmit(onSubmit, () =>
              toast.error(
                t("customRegistration.validation.fixMarkedFields") as string
              )
            )}
            className="space-y-4 sm:space-y-6 mt-3 sm:mt-4"
            noValidate
          >
            <fieldset
              disabled={!canWrite}
              className="border-0 m-0 p-0 min-w-0 space-y-4 sm:space-y-6"
            >
              <Surface>
                <div className="mb-3 sm:mb-4">
                  <p className="text-gray-500 text-sm leading-tight">
                    {t("customRegistration.coreFields.formDescription")}
                  </p>
                </div>
                <div className="space-y-2" translate="no">
                  <Controller
                    name="formDescription"
                    control={control}
                    render={({ field }) => (
                      <Textarea
                        {...field}
                        placeholder={
                          t(
                            "customRegistration.coreFields.formDescriptionPlaceholder"
                          ) as string
                        }
                        className="w-full resize-none"
                        rows={3}
                        maxLength={200}
                        value={field.value || ""}
                      />
                    )}
                  />
                  <CharacterCounter
                    currentLength={watch("formDescription")?.length || 0}
                    maxLength={200}
                  />
                </div>
              </Surface>

              <SectionHeading
                step={2}
                label={t("customRegistration.coreFields.title")}
              />
              <CoreFieldsSection control={control} />
              <SectionHeading
                step={3}
                label={t("customRegistration.customQuestions.title")}
              />
              <CustomQuestionsSection
                control={control}
                errors={errors}
                readOnly={!canWrite}
              />
              {canConfigureBooking && (
                <>
                  <SectionHeading
                    step={4}
                    label={t("customRegistration.booking.stepLabel")}
                  />
                  <BookingSection
                    control={control}
                    errors={errors}
                    readOnly={!canWrite}
                  />
                </>
              )}
              <SectionHeading
                step={4 + bookingStep}
                label={t("customRegistration.payment.title")}
              />
              <PaymentSection
                control={control}
                watch={watch}
                errors={errors}
                setValue={setValue}
                readOnly={!canWrite}
              />
              <SectionHeading
                step={5 + bookingStep}
                label={t("customRegistration.postActions.title")}
              />
              <PostRegistrationActionsSection
                control={control}
                watch={watch}
                errors={errors}
                setValue={setValue}
                hasRedirectUrlAccess={hasRedirectUrlAccess}
                onUpgradeClick={() => setShowRedirectUpgradeModal(true)}
                readOnly={!canWrite}
              />
              <SectionHeading
                step={6 + bookingStep}
                label={t("customRegistration.automatedEmail.stepLabel")}
              />
              <AutomatedEmailSection
                key={`automated-email-${
                  formConfig?.automatedEmail?.attachments?.length || 0
                }`}
                control={control}
                watch={watch}
                errors={errors}
                setValue={setValue}
                onCorrect={handleCorrectEmailContent}
                isCorrectingMessage={isCorrectingMessage}
                onPreview={handlePreviewEmail}
                isRequestingPreview={isRequestingPreview}
                readOnly={!canWrite}
              />
              <SectionHeading
                step={7 + bookingStep}
                label={t("customRegistration.esignature.stepLabel")}
              />
              <EsignatureSection
                control={control}
                errors={errors}
                setValue={setValue}
                readOnly={!canWrite}
              />

              <UtmTrackingSection />
            </fieldset>

            <div className="flex flex-col sm:flex-row w-full gap-3 sm:justify-between">
              {permissions.can(Cap.SIGNUP_FORMS, Level.WRITE) && (
                <CustomButton
                  variant="secondary"
                  title={t("dashboard.addUserByShare.title-02")}
                  className="px-4 sm:px-6 w-full sm:w-auto"
                  onClick={handleGenerateLink}
                  isLoading={isUpdating}
                  disabled={isUpdating}
                />
              )}

              {permissions.can(Cap.SIGNUP_FORMS, Level.WRITE) && (
                <CustomButton
                  isLoading={isUpdating}
                  variant="primary"
                  type="submit"
                  title={t("common.save") as string}
                  className="px-4 sm:px-6 w-full sm:w-auto"
                  disabled={isSubmitting || isUpdating}
                />
              )}
            </div>
          </form>
        </div>
      </ResponsivePreviewLayout>

      <ShareModal
        showShareModal={showShareModal}
        setShowShareModal={setShowShareModal}
        processId={processId}
        generatedLink={generatedLink}
      />

      <ModalShell
        dismissible
        show={showEmailPreview}
        onClose={() => setShowEmailPreview(false)}
        size="5xl"
        title={t("dashboard.entities.viewEntityUser.sendMsg.key2")}
        bodyClassName="p-0"
      >
        <EmailPreviewFrame
          html={emailPreviewHtml}
          title={t("dashboard.entities.viewEntityUser.sendMsg.key2") as string}
        />
      </ModalShell>

      <PlansModal
        showPlansModal={showRedirectUpgradeModal}
        setShowPlansModal={setShowRedirectUpgradeModal}
        targetPlan={PlanTier.PREMIUM}
        hideFooter={false}
      />
    </div>
  );
}
